// FILE: Sidebar.logic.ts
// Purpose: Shared sidebar sorting and status helpers used by the thread list UI.
// Exports: Sidebar row state derivation, add-project error helpers, sort utilities, and visibility helpers.

import type { SidebarProjectSortOrder, SidebarThreadSortOrder } from "../appSettings";
import type { ChatMessage, Project, SidebarThreadSummary, Thread } from "../types";
import { cn } from "../lib/utils";
import { isDelegateThread } from "@taskin-code/shared/delegation";
import { workspaceRootsEqual } from "@taskin-code/shared/threadWorkspace";
import {
  hasLiveLatestTurn,
  findLatestProposedPlan,
  hasActionableProposedPlan,
  isLatestTurnSettled,
  type DelegationState,
} from "../session-logic";
import { threadReturnedFromSnooze } from "../lib/threadInboxState";

export const THREAD_SELECTION_SAFE_SELECTOR = "[data-thread-item], [data-thread-selection-safe]";
export type SidebarNewThreadEnvMode = "local" | "worktree";
type SidebarProject = {
  id: string;
  name: string;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
};
type SidebarThreadSortInput = {
  createdAt: string;
  updatedAt?: string | undefined;
  latestUserMessageAt?: string | null | undefined;
  messages?: ReadonlyArray<Pick<ChatMessage, "role" | "createdAt">> | undefined;
};

const DUPLICATE_PROJECT_CREATE_ERROR_PREFIX =
  "Orchestration command invariant failed (project.create): Project '";

export interface ThreadStatusPill {
  label:
    | "Working"
    | "Connecting"
    | "Completed"
    | "Pending Approval"
    | "Awaiting Input"
    | "Plan Ready"
    | "Delegated"
    | "Delegate Ready"
    | "Back From Snooze";
  colorClass: string;
  dotClass: string;
  pulse: boolean;
  /**
   * Asks for the spinner to be drawn in the pill's own colour instead of the
   * neutral one. Only delegation uses it: "Working" (the thread itself running)
   * has to stay visually what it has always been, and a second spinner in the
   * same grey would be indistinguishable from it.
   */
  tintedSpinner?: boolean;
  /** Extra count for the row's tooltip, e.g. how many delegations are running. */
  detail?: string;
}

const THREAD_STATUS_PRIORITY: Record<ThreadStatusPill["label"], number> = {
  "Pending Approval": 7,
  "Awaiting Input": 6,
  Working: 5,
  Connecting: 5,
  "Delegate Ready": 4,
  Delegated: 3,
  "Plan Ready": 2,
  Completed: 1,
  // Last on purpose. This one exists so a project with a thread that came back
  // from a pause is not left looking empty when the project is collapsed — it
  // is a reminder, not news. Anything that is actual news outranks it, and
  // "Completed" in particular: a turn that finished during the pause is the
  // more useful thing to say, and saying both is not an option in one dot.
  "Back From Snooze": 0,
};

type ThreadStatusInput = Pick<
  Thread,
  "interactionMode" | "latestTurn" | "lastVisitedAt" | "session"
> & {
  /**
   * What kind of child thread this is, when it is one. Read only through
   * [[isDelegateThread]]: a delegation's own finish line is its parent's
   * business, not the user's.
   */
  subagentRole?: string | null | undefined;
  /**
   * This reader's own snooze, so the row can say why the thread reappeared.
   * See `SidebarThreadSummary.wokenAt`.
   */
  snoozedUntil?: string | null | undefined;
  wokenAt?: string | null | undefined;
  /** See `SidebarThreadSummary.delegationWakeSkippedAt`. */
  delegationWakeSkippedAt?: string | null | undefined;
  proposedPlans?: Thread["proposedPlans"] | undefined;
  hasActionableProposedPlan?: boolean | undefined;
  hasLiveTailWork?: boolean | undefined;
  /**
   * What this thread's delegations are doing. Absent for a thread that has none,
   * which is every thread that never delegated.
   */
  delegation?: DelegationState | undefined;
};

/**
 * Whether this thread finishing is news the user has not seen yet.
 *
 * A delegation is excluded outright rather than judged by `lastVisitedAt`: the
 * user is not expected to open it at all, so its completion would sit unread
 * forever and the row would never stop asking. The parent reports for it — and
 * when the parent cannot, [[hasStalledDelegationReport]] does.
 */
export function hasUnseenCompletion(thread: ThreadStatusInput): boolean {
  if (isDelegateThread(thread)) return false;
  if (!thread.latestTurn?.completedAt) return false;
  const completedAt = Date.parse(thread.latestTurn.completedAt);
  if (Number.isNaN(completedAt)) return false;
  if (!thread.lastVisitedAt) return true;

  const lastVisitedAt = Date.parse(thread.lastVisitedAt);
  if (Number.isNaN(lastVisitedAt)) return true;
  return completedAt > lastVisitedAt;
}

export function shouldClearThreadSelectionOnMouseDown(target: HTMLElement | null): boolean {
  if (target === null) return true;
  return !target.closest(THREAD_SELECTION_SAFE_SELECTOR);
}

export function resolveSidebarNewThreadEnvMode(input: {
  requestedEnvMode?: SidebarNewThreadEnvMode;
  defaultEnvMode: SidebarNewThreadEnvMode;
  projectIsBareRepository?: boolean;
}): SidebarNewThreadEnvMode {
  // Cloned-from-GitHub projects have a bare workspaceRoot. A thread cannot
  // run against a bare repository directly (it has no working tree), so we
  // force `worktree` mode regardless of the user's default or any explicit
  // request — the worktree is the only valid execution surface here.
  if (input.projectIsBareRepository) return "worktree";
  return input.requestedEnvMode ?? input.defaultEnvMode;
}

/**
 * `"row"` is the single-line thread row used by the per-project lists.
 * `"card"` is the two-line block used by the "All projects" list, where each row
 * also carries a project header line. Only the layout differs — the
 * active/selected palette is shared so both modes highlight identically.
 */
export type ThreadRowVariant = "row" | "card";

const THREAD_ROW_BASE_CLASS_NAME_BY_VARIANT: Record<ThreadRowVariant, string> = {
  row: "h-8 w-full translate-x-0 cursor-pointer justify-start rounded-md pr-9 pl-8 text-left text-[13px] select-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring",
  card: "h-auto w-full translate-x-0 cursor-pointer flex-col items-stretch gap-0.5 rounded-md py-1.5 pr-9 pl-8 text-left text-[13px] select-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring",
};

export function resolveThreadRowClassName(input: {
  isActive: boolean;
  isSelected: boolean;
  variant?: ThreadRowVariant | undefined;
}): string {
  const baseClassName = THREAD_ROW_BASE_CLASS_NAME_BY_VARIANT[input.variant ?? "row"];

  if (input.isSelected && input.isActive) {
    return cn(
      baseClassName,
      "bg-primary/16 text-foreground/90 hover:bg-primary/20 hover:text-foreground dark:bg-primary/22 dark:hover:bg-primary/28",
    );
  }

  if (input.isSelected) {
    return cn(
      baseClassName,
      "bg-primary/12 text-foreground/88 hover:bg-primary/16 hover:text-foreground dark:bg-primary/18 dark:hover:bg-primary/24",
    );
  }

  if (input.isActive) {
    return cn(
      baseClassName,
      "bg-accent/62 text-foreground/90 hover:bg-accent/72 hover:text-foreground dark:bg-accent/42 dark:hover:bg-accent/56",
    );
  }

  return cn(baseClassName, "text-foreground/78 hover:bg-accent/45 hover:text-foreground");
}

export function resolveThreadStatusPill(input: {
  thread: ThreadStatusInput;
  hasPendingApprovals: boolean;
  hasPendingUserInput: boolean;
}): ThreadStatusPill | null {
  const { hasPendingApprovals, hasPendingUserInput, thread } = input;

  if (hasPendingApprovals) {
    return {
      label: "Pending Approval",
      colorClass: "text-amber-600 dark:text-amber-300/90",
      dotClass: "bg-amber-500 dark:bg-amber-300/90",
      pulse: false,
    };
  }

  if (hasPendingUserInput) {
    return {
      label: "Awaiting Input",
      colorClass: "text-indigo-600 dark:text-indigo-300/90",
      dotClass: "bg-indigo-500 dark:bg-indigo-300/90",
      pulse: false,
    };
  }

  if (thread.hasLiveTailWork) {
    return {
      label: "Working",
      colorClass: "text-sky-600 dark:text-sky-300/80",
      dotClass: "bg-sky-500 dark:bg-sky-300/80",
      pulse: true,
    };
  }

  if (
    thread.session?.status === "running" &&
    (thread.latestTurn === null || hasLiveLatestTurn(thread.latestTurn, thread.session))
  ) {
    return {
      label: "Working",
      colorClass: "text-sky-600 dark:text-sky-300/80",
      dotClass: "bg-sky-500 dark:bg-sky-300/80",
      pulse: true,
    };
  }

  if (thread.session?.status === "connecting") {
    return {
      label: "Connecting",
      colorClass: "text-sky-600 dark:text-sky-300/80",
      dotClass: "bg-sky-500 dark:bg-sky-300/80",
      pulse: true,
    };
  }

  // Delegation states come after the thread's own work and before anything that
  // reads as finished: a thread whose delegate is still going has a settled turn
  // of its own, so without these it would show "Completed" while work runs.
  const delegation = thread.delegation;
  if (delegation) {
    if (delegation.readyCount > 0) {
      return {
        label: "Delegate Ready",
        colorClass: "text-amber-600 dark:text-amber-300/90",
        dotClass: "bg-amber-500 dark:bg-amber-300/90",
        pulse: false,
        detail:
          delegation.readyCount === 1
            ? "1 delegación entregó su informe"
            : `${delegation.readyCount} delegaciones entregaron su informe`,
      };
    }
    if (delegation.liveCount > 0 || delegation.hasLiveDescendants) {
      return {
        label: "Delegated",
        colorClass: "text-violet-600 dark:text-violet-300/85",
        dotClass: "bg-violet-500 dark:bg-violet-300/85",
        pulse: true,
        tintedSpinner: true,
        detail:
          delegation.liveCount > 0
            ? delegation.liveCount === 1
              ? "1 delegación trabajando"
              : `${delegation.liveCount} delegaciones trabajando`
            : "Trabajo en curso en una subdelegación",
      };
    }
  }

  const hasPlanReadyPrompt =
    !hasPendingUserInput &&
    !thread.hasLiveTailWork &&
    thread.interactionMode === "plan" &&
    isLatestTurnSettled(thread.latestTurn, thread.session) &&
    (thread.hasActionableProposedPlan ??
      hasActionableProposedPlan(
        findLatestProposedPlan(thread.proposedPlans ?? [], thread.latestTurn?.turnId ?? null),
      ));
  if (hasPlanReadyPrompt) {
    return {
      label: "Plan Ready",
      colorClass: "text-violet-600 dark:text-violet-300/90",
      dotClass: "bg-violet-500 dark:bg-violet-300/90",
      pulse: false,
    };
  }

  if (!thread.hasLiveTailWork && hasUnseenCompletion(thread)) {
    return {
      label: "Completed",
      colorClass: "text-emerald-600 dark:text-emerald-300/90",
      dotClass: "bg-emerald-500 dark:bg-emerald-300/90",
      pulse: false,
    };
  }

  // The deadline someone set ran out and nobody has opened the thread since.
  // The row already says so in words, but the project row only has this dot,
  // and a collapsed project with a thread waiting to be picked back up looked
  // exactly like one with nothing in it.
  if (threadReturnedFromSnooze(thread)) {
    return {
      label: "Back From Snooze",
      colorClass: "text-teal-600 dark:text-teal-300/85",
      dotClass: "bg-teal-500 dark:bg-teal-300/85",
      pulse: false,
      detail: "Volvió de pospuesto",
    };
  }

  return null;
}

export type ThreadAttentionReason =
  | "pending-approval"
  | "awaiting-input"
  | "terminal-attention"
  | "delegate-ready"
  | "turn-completed";

export interface ThreadAttentionIndicator {
  reason: ThreadAttentionReason;
  label: string;
  colorClass: string;
  pulse: boolean;
}

type TerminalAttentionState = "attention" | "review";

/**
 * Whether a delegating thread is stuck holding a report nobody will pick up.
 *
 * Not "a report is unread": the wake reactor resumes the parent by itself, and
 * every verdict it holds back on (`parent-busy`, `delegations-still-working`)
 * resolves on the next sweep — except the one that writes
 * `delegation.wake.skipped`, where it has decided to stop trying. Judging by
 * `readyCount` alone made the sidebar demand attention for the ordinary case:
 * a report that would be read seconds later, on its own.
 *
 * The stamp is compared against the parent's newest turn so the badge clears the
 * way it was earned: writing in the thread starts a turn after the decision, and
 * the agent picking the report up drops `readyCount` to zero.
 */
export function hasStalledDelegationReport(input: {
  latestTurn?: Thread["latestTurn"] | undefined;
  delegation?: DelegationState | undefined;
  delegationWakeSkippedAt?: string | null | undefined;
}): boolean {
  if ((input.delegation?.readyCount ?? 0) === 0) return false;
  const skippedAt = input.delegationWakeSkippedAt;
  if (!skippedAt) return false;
  const startedAt = input.latestTurn?.startedAt;
  if (!startedAt) return true;
  return skippedAt > startedAt;
}

export function resolveThreadNeedsAttention(input: {
  thread: ThreadStatusInput & {
    hasPendingApprovals?: boolean;
    hasPendingUserInput?: boolean;
  };
  isActive: boolean;
  terminalAttentionStates?: ReadonlyArray<TerminalAttentionState> | undefined;
}): ThreadAttentionIndicator | null {
  if (input.isActive) return null;

  if (input.thread.hasPendingApprovals) {
    return {
      reason: "pending-approval",
      label: "Requiere aprobación",
      colorClass: "text-amber-500 dark:text-amber-300/90",
      pulse: true,
    };
  }

  if (input.thread.hasPendingUserInput) {
    return {
      reason: "awaiting-input",
      label: "Requiere tu respuesta",
      colorClass: "text-indigo-500 dark:text-indigo-300/90",
      pulse: true,
    };
  }

  const terminalStates = input.terminalAttentionStates ?? [];
  if (terminalStates.includes("attention")) {
    return {
      reason: "terminal-attention",
      label: "Terminal necesita tu atención",
      colorClass: "text-amber-500 dark:text-amber-300/90",
      pulse: true,
    };
  }

  // A report the server has given up on resuming: nothing else will move this
  // thread, so it is the one delegation state that genuinely needs a person.
  if (hasStalledDelegationReport(input.thread)) {
    return {
      reason: "delegate-ready",
      label: "Una delegación quedó sin reanudar",
      colorClass: "text-amber-500 dark:text-amber-300/90",
      pulse: false,
    };
  }

  if (!input.thread.hasLiveTailWork && hasUnseenCompletion(input.thread)) {
    return {
      reason: "turn-completed",
      label: "Nueva respuesta sin leer",
      colorClass: "text-emerald-500 dark:text-emerald-300/90",
      pulse: false,
    };
  }

  return null;
}

export function resolveProjectStatusIndicator(
  statuses: ReadonlyArray<ThreadStatusPill | null>,
): ThreadStatusPill | null {
  let highestPriorityStatus: ThreadStatusPill | null = null;

  for (const status of statuses) {
    if (status === null) continue;
    if (
      highestPriorityStatus === null ||
      THREAD_STATUS_PRIORITY[status.label] > THREAD_STATUS_PRIORITY[highestPriorityStatus.label]
    ) {
      highestPriorityStatus = status;
    }
  }

  return highestPriorityStatus;
}

/**
 * The states that mean the thread is waiting on the person, as opposed to
 * merely being busy.
 *
 * "Working", "Connecting" and "Delegated" are deliberately out: an agent
 * running is the normal state of a project and counting it would leave every
 * collapsed row wearing a number that never goes away. What is left is the set
 * of things somebody has to come back and look at.
 */
const THREAD_STATUS_NEEDS_PERSON: ReadonlySet<ThreadStatusPill["label"]> = new Set([
  "Pending Approval",
  "Awaiting Input",
  "Plan Ready",
  "Delegate Ready",
  "Completed",
  "Back From Snooze",
]);

/**
 * How many of a project's threads are waiting on the person.
 *
 * Only the collapsed row uses it. Expanded, every thread shows its own dot and
 * a number on the folder would just restate them; collapsed, the dot was the
 * whole signal and it says "at least one" whether the answer is one or nine.
 */
export function countProjectThreadsNeedingAttention(
  statuses: ReadonlyArray<ThreadStatusPill | null>,
): number {
  let count = 0;
  for (const status of statuses) {
    if (status !== null && THREAD_STATUS_NEEDS_PERSON.has(status.label)) count += 1;
  }
  return count;
}

export function findWorkspaceRootMatch<T>(
  items: readonly T[],
  targetWorkspaceRoot: string,
  getWorkspaceRoot: (item: T) => string,
): T | undefined {
  return items.find((item) => workspaceRootsEqual(getWorkspaceRoot(item), targetWorkspaceRoot));
}

// Detects duplicate workspace-root failures so the UI can add a human explanation.
export function isDuplicateProjectCreateError(message: string): boolean {
  if (!message.startsWith(DUPLICATE_PROJECT_CREATE_ERROR_PREFIX)) {
    return false;
  }

  const duplicateMarkerIndex = message.indexOf("' already uses workspace root '");
  return duplicateMarkerIndex > DUPLICATE_PROJECT_CREATE_ERROR_PREFIX.length;
}

export function extractDuplicateProjectCreateProjectId(message: string): string | null {
  if (!isDuplicateProjectCreateError(message)) {
    return null;
  }

  const duplicateMarkerIndex = message.indexOf("' already uses workspace root '");
  return message.slice(DUPLICATE_PROJECT_CREATE_ERROR_PREFIX.length, duplicateMarkerIndex) || null;
}

// Translates low-level add-project failures into a short explanation without
// hiding the original error text that developers may need for diagnosis.
export function describeAddProjectError(message: string): string | null {
  if (isDuplicateProjectCreateError(message)) {
    return "This usually means the folder is already linked to an existing project. On Windows, the same folder can arrive with a different path format, so it looks new even when it is not.";
  }

  return null;
}

export function getVisibleThreadsForPreview<T extends Pick<SidebarThreadSummary, "id">>(input: {
  threads: readonly T[];
  activeThreadId: Thread["id"] | undefined;
  isThreadListExpanded: boolean;
  previewLimit: number;
}): {
  hasHiddenThreads: boolean;
  visibleThreads: T[];
} {
  const { activeThreadId, isThreadListExpanded, previewLimit, threads } = input;
  const hasHiddenThreads = threads.length > previewLimit;

  if (!hasHiddenThreads || isThreadListExpanded) {
    return {
      hasHiddenThreads,
      visibleThreads: [...threads],
    };
  }

  const previewThreads = threads.slice(0, previewLimit);
  if (!activeThreadId || previewThreads.some((thread) => thread.id === activeThreadId)) {
    return {
      hasHiddenThreads: true,
      visibleThreads: previewThreads,
    };
  }

  const activeThread = threads.find((thread) => thread.id === activeThreadId);
  if (!activeThread) {
    return {
      hasHiddenThreads: true,
      visibleThreads: previewThreads,
    };
  }

  const visibleThreadIds = new Set([...previewThreads, activeThread].map((thread) => thread.id));

  return {
    hasHiddenThreads: true,
    visibleThreads: threads.filter((thread) => visibleThreadIds.has(thread.id)),
  };
}

export interface SidebarThreadTreeRow<
  T extends Pick<SidebarThreadSummary, "id" | "parentThreadId">,
> {
  thread: T;
  depth: number;
  rootThreadId: T["id"];
  childCount: number;
  isExpanded: boolean;
}

function collectForcedExpandedParentIds<
  T extends Pick<SidebarThreadSummary, "id" | "parentThreadId">,
>(threadById: Map<T["id"], T>, forceVisibleThreadId: T["id"] | undefined): Set<T["id"]> {
  const forcedParentIds = new Set<T["id"]>();
  let currentThreadId = forceVisibleThreadId;

  while (currentThreadId) {
    const parentThreadId = threadById.get(currentThreadId)?.parentThreadId ?? undefined;
    if (!parentThreadId) {
      break;
    }
    forcedParentIds.add(parentThreadId);
    currentThreadId = parentThreadId;
  }

  return forcedParentIds;
}

// Build the project-local parent/child thread tree while preserving sort order from the input list.
export function buildProjectThreadTree<
  T extends Pick<SidebarThreadSummary, "id" | "parentThreadId">,
>(input: {
  threads: readonly T[];
  expandedParentThreadIds?: ReadonlySet<T["id"]> | undefined;
  forceVisibleThreadId?: T["id"] | undefined;
}): SidebarThreadTreeRow<T>[] {
  const { expandedParentThreadIds, forceVisibleThreadId, threads } = input;
  const threadById = new Map(threads.map((thread) => [thread.id, thread] as const));
  const childrenByParentId = new Map<T["id"], T[]>();
  const roots: T[] = [];

  for (const thread of threads) {
    const parentThreadId = thread.parentThreadId ?? null;
    if (!parentThreadId || !threadById.has(parentThreadId)) {
      roots.push(thread);
      continue;
    }
    const siblings = childrenByParentId.get(parentThreadId) ?? [];
    siblings.push(thread);
    childrenByParentId.set(parentThreadId, siblings);
  }

  const forcedExpandedParentIds = collectForcedExpandedParentIds(threadById, forceVisibleThreadId);
  const orderedRows: SidebarThreadTreeRow<T>[] = [];

  const visit = (thread: T, depth: number, rootThreadId: T["id"]) => {
    const childThreads = childrenByParentId.get(thread.id) ?? [];
    const isExpanded =
      childThreads.length > 0 &&
      (expandedParentThreadIds?.has(thread.id) === true || forcedExpandedParentIds.has(thread.id));

    orderedRows.push({
      thread,
      depth,
      rootThreadId,
      childCount: childThreads.length,
      isExpanded,
    });

    if (!isExpanded) {
      return;
    }

    for (const child of childThreads) {
      visit(child, depth + 1, rootThreadId);
    }
  };

  for (const root of roots) {
    visit(root, 0, root.id);
  }

  return orderedRows;
}

export function getVisibleSidebarEntriesForPreview<
  T extends {
    rowId: Thread["id"];
    rootRowId: Thread["id"];
  },
>(input: {
  entries: readonly T[];
  activeEntryId: Thread["id"] | undefined;
  isExpanded: boolean;
  previewLimit: number;
}): {
  hasHiddenEntries: boolean;
  visibleEntries: T[];
} {
  const { activeEntryId, entries, isExpanded, previewLimit } = input;
  const orderedRootRowIds: Thread["id"][] = [];
  const seenRootRowIds = new Set<Thread["id"]>();

  for (const entry of entries) {
    if (seenRootRowIds.has(entry.rootRowId)) {
      continue;
    }
    seenRootRowIds.add(entry.rootRowId);
    orderedRootRowIds.push(entry.rootRowId);
  }

  const hasHiddenEntries = orderedRootRowIds.length > previewLimit;
  if (!hasHiddenEntries || isExpanded) {
    return {
      hasHiddenEntries,
      visibleEntries: [...entries],
    };
  }

  const visibleRootRowIds = new Set(orderedRootRowIds.slice(0, previewLimit));
  const activeRootRowId =
    activeEntryId !== undefined
      ? (entries.find((entry) => entry.rowId === activeEntryId)?.rootRowId ?? null)
      : null;

  if (activeRootRowId) {
    visibleRootRowIds.add(activeRootRowId);
  }

  return {
    hasHiddenEntries: true,
    visibleEntries: entries.filter((entry) => visibleRootRowIds.has(entry.rootRowId)),
  };
}

// Preserve the persisted pin order while discarding ids that no longer exist locally.
export function getPinnedThreadsForSidebar<T extends Pick<Thread, "id">>(
  threads: readonly T[],
  pinnedThreadIds: readonly T["id"][],
): T[] {
  const threadById = new Map(threads.map((thread) => [thread.id, thread] as const));
  const seen = new Set<T["id"]>();
  const pinnedThreads: T[] = [];

  for (const threadId of pinnedThreadIds) {
    if (seen.has(threadId)) continue;
    seen.add(threadId);
    const thread = threadById.get(threadId);
    if (thread) {
      pinnedThreads.push(thread);
    }
  }

  return pinnedThreads;
}

// Hide globally pinned rows from the per-project lists so the sidebar doesn't duplicate chats.
export function getUnpinnedThreadsForSidebar<T extends Pick<Thread, "id">>(
  threads: readonly T[],
  pinnedThreadIds: readonly T["id"][],
): T[] {
  if (pinnedThreadIds.length === 0) {
    return [...threads];
  }

  const pinnedThreadIdSet = new Set(pinnedThreadIds);
  return threads.filter((thread) => !pinnedThreadIdSet.has(thread.id));
}

// Only prune persisted pins after the thread snapshot has hydrated.
export function shouldPrunePinnedThreads(input: { threadsHydrated: boolean }): boolean {
  return input.threadsHydrated;
}

// Match the exact rows the sidebar renders for one project, including folded previews.
export function getRenderedThreadsForSidebarProject<
  T extends Pick<SidebarThreadSummary, "id"> & SidebarThreadSortInput,
>(input: {
  project: Pick<Project, "expanded">;
  threads: readonly T[];
  activeThreadId: Thread["id"] | undefined;
  isThreadListExpanded: boolean;
  previewLimit: number;
}): {
  hasHiddenThreads: boolean;
  renderedThreads: T[];
} {
  const { activeThreadId, isThreadListExpanded, previewLimit, project, threads } = input;
  const pinnedCollapsedThread =
    !project.expanded && activeThreadId
      ? (threads.find((thread) => thread.id === activeThreadId) ?? null)
      : null;
  const { hasHiddenThreads, visibleThreads } = getVisibleThreadsForPreview({
    threads,
    activeThreadId,
    isThreadListExpanded,
    previewLimit,
  });

  return {
    hasHiddenThreads,
    renderedThreads: pinnedCollapsedThread ? [pinnedCollapsedThread] : visibleThreads,
  };
}

export interface SidebarAllProjectsThreadList<T> {
  /** Every row the flat list can show, in render order. */
  orderedThreads: T[];
  /** The subset currently rendered once the folded preview limit is applied. */
  visibleThreads: T[];
  hasHiddenThreads: boolean;
}

/**
 * "All projects" mode renders one flat, date-ordered list instead of per-project
 * groups. Children stay out of it, and this filter is what keeps them out now
 * that delegations do reach the sidebar: the flat list never nests, so a child
 * let through would be rendered as a root row, next to its own parent and
 * stripped of the only thing that explains it. They stay reachable in the
 * per-project tree, and from the running-subagents indicator. Pinned rows are
 * dropped because the pinned section above already shows them.
 *
 * The preview fold reuses `getVisibleThreadsForPreview`: the helper is generic
 * over any thread list, so the flat list gets the same "keep the active thread
 * visible" behaviour the per-project lists have.
 */
export function getAllProjectsFlatThreadList<
  T extends Pick<SidebarThreadSummary, "id" | "parentThreadId"> & SidebarThreadSortInput,
>(input: {
  threads: readonly T[];
  pinnedThreadIds?: readonly T["id"][] | undefined;
  activeThreadId?: Thread["id"] | undefined;
  isExpanded: boolean;
  previewLimit: number;
  threadSortOrder: SidebarThreadSortOrder;
}): SidebarAllProjectsThreadList<T> {
  const orderedThreads = sortThreadsForSidebar(
    getUnpinnedThreadsForSidebar(
      input.threads.filter((thread) => !thread.parentThreadId),
      input.pinnedThreadIds ?? [],
    ),
    input.threadSortOrder,
  );
  const { hasHiddenThreads, visibleThreads } = getVisibleThreadsForPreview({
    threads: orderedThreads,
    activeThreadId: input.activeThreadId,
    isThreadListExpanded: input.isExpanded,
    previewLimit: input.previewLimit,
  });

  return { orderedThreads, visibleThreads, hasHiddenThreads };
}

// Flatten the sidebar's current project/thread visibility into the same order the user sees.
export function getVisibleSidebarThreadIds(input: {
  projects: readonly Pick<Project, "id" | "expanded">[];
  threads: readonly (Pick<SidebarThreadSummary, "id" | "projectId" | "parentThreadId"> &
    SidebarThreadSortInput)[];
  activeThreadId: Thread["id"] | undefined;
  expandedThreadListsByProject: ReadonlySet<Project["id"]>;
  expandedSubagentParentIds?: ReadonlySet<Thread["id"]>;
  previewLimit: number;
  threadSortOrder: SidebarThreadSortOrder;
}): Thread["id"][] {
  const {
    activeThreadId,
    expandedSubagentParentIds,
    expandedThreadListsByProject,
    previewLimit,
    projects,
    threadSortOrder,
    threads,
  } = input;
  const visibleThreadIds: Thread["id"][] = [];

  for (const project of projects) {
    const projectThreads = sortThreadsForSidebar(
      threads.filter((thread) => thread.projectId === project.id),
      threadSortOrder,
    );
    const projectThreadTree = buildProjectThreadTree({
      threads: projectThreads,
      expandedParentThreadIds: expandedSubagentParentIds,
    });
    const { visibleEntries } = getVisibleSidebarEntriesForPreview({
      entries: projectThreadTree.map((row) => ({
        rowId: row.thread.id,
        rootRowId: row.rootThreadId,
        threadId: row.thread.id,
      })),
      activeEntryId: activeThreadId,
      isExpanded: expandedThreadListsByProject.has(project.id),
      previewLimit,
    });
    const pinnedCollapsedThread =
      !project.expanded && activeThreadId
        ? (projectThreads.find((thread) => thread.id === activeThreadId) ?? null)
        : null;

    if (pinnedCollapsedThread) {
      visibleThreadIds.push(pinnedCollapsedThread.id);
      continue;
    }

    for (const entry of visibleEntries) {
      visibleThreadIds.push(entry.threadId);
    }
  }

  return visibleThreadIds;
}

// Resolve the next sidebar-visible thread for keyboard cycling with wraparound.
export function getNextVisibleSidebarThreadId(input: {
  visibleThreadIds: readonly Thread["id"][];
  activeThreadId: Thread["id"] | undefined;
  direction: "forward" | "backward";
}): Thread["id"] | null {
  const { activeThreadId, direction, visibleThreadIds } = input;
  if (visibleThreadIds.length === 0) {
    return null;
  }

  if (!activeThreadId) {
    return direction === "forward"
      ? (visibleThreadIds[0] ?? null)
      : (visibleThreadIds.at(-1) ?? null);
  }

  const activeIndex = visibleThreadIds.findIndex((threadId) => threadId === activeThreadId);
  if (activeIndex === -1) {
    return direction === "forward"
      ? (visibleThreadIds[0] ?? null)
      : (visibleThreadIds.at(-1) ?? null);
  }

  const nextIndex =
    direction === "forward"
      ? (activeIndex + 1) % visibleThreadIds.length
      : (activeIndex - 1 + visibleThreadIds.length) % visibleThreadIds.length;

  return visibleThreadIds[nextIndex] ?? null;
}

function toSortableTimestamp(iso: string | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

function getLatestUserMessageTimestamp(thread: SidebarThreadSortInput): number {
  const latestUserMessageAt = toSortableTimestamp(thread.latestUserMessageAt ?? undefined);
  if (latestUserMessageAt !== null) {
    return latestUserMessageAt;
  }

  let latestUserMessageTimestamp: number | null = null;

  for (const message of thread.messages ?? []) {
    if (message.role !== "user") continue;
    const messageTimestamp = toSortableTimestamp(message.createdAt);
    if (messageTimestamp === null) continue;
    latestUserMessageTimestamp =
      latestUserMessageTimestamp === null
        ? messageTimestamp
        : Math.max(latestUserMessageTimestamp, messageTimestamp);
  }

  if (latestUserMessageTimestamp !== null) {
    return latestUserMessageTimestamp;
  }

  return toSortableTimestamp(thread.updatedAt ?? thread.createdAt) ?? Number.NEGATIVE_INFINITY;
}

function getThreadSortTimestamp(
  thread: SidebarThreadSortInput,
  sortOrder: SidebarThreadSortOrder | Exclude<SidebarProjectSortOrder, "manual">,
): number {
  if (sortOrder === "created_at") {
    return toSortableTimestamp(thread.createdAt) ?? Number.NEGATIVE_INFINITY;
  }
  return getLatestUserMessageTimestamp(thread);
}

/**
 * ISO timestamp the sidebar is currently sorting by, so a row that displays a
 * time explains its own position in the list (recency mode shows the latest user
 * message, creation mode shows the creation time). Returns null when the thread
 * has nothing sortable and the caller should fall back to its own timestamps.
 */
export function getSidebarThreadSortIsoTimestamp(
  thread: SidebarThreadSortInput,
  sortOrder: SidebarThreadSortOrder,
): string | null {
  const timestamp = getThreadSortTimestamp(thread, sortOrder);
  if (!Number.isFinite(timestamp)) {
    return null;
  }
  return new Date(timestamp).toISOString();
}

export function sortThreadsForSidebar<T extends { id: Thread["id"] } & SidebarThreadSortInput>(
  threads: readonly T[],
  sortOrder: SidebarThreadSortOrder,
): T[] {
  return threads.toSorted((left, right) => {
    const rightTimestamp = getThreadSortTimestamp(right, sortOrder);
    const leftTimestamp = getThreadSortTimestamp(left, sortOrder);
    const byTimestamp =
      rightTimestamp === leftTimestamp ? 0 : rightTimestamp > leftTimestamp ? 1 : -1;
    if (byTimestamp !== 0) return byTimestamp;
    return right.id.localeCompare(left.id);
  });
}

export function getFallbackThreadIdAfterDelete<
  T extends { id: Thread["id"]; projectId: Thread["projectId"] } & SidebarThreadSortInput,
>(input: {
  threads: readonly T[];
  deletedThreadId: T["id"];
  sortOrder: SidebarThreadSortOrder;
  deletedThreadIds?: ReadonlySet<T["id"]>;
}): T["id"] | null {
  const { deletedThreadId, deletedThreadIds, sortOrder, threads } = input;
  const deletedThread = threads.find((thread) => thread.id === deletedThreadId);
  if (!deletedThread) {
    return null;
  }

  return (
    sortThreadsForSidebar(
      threads.filter(
        (thread) =>
          thread.projectId === deletedThread.projectId &&
          thread.id !== deletedThreadId &&
          !deletedThreadIds?.has(thread.id),
      ),
      sortOrder,
    )[0]?.id ?? null
  );
}

export function getProjectSortTimestamp(
  project: SidebarProject,
  projectThreads: readonly SidebarThreadSortInput[],
  sortOrder: Exclude<SidebarProjectSortOrder, "manual">,
): number {
  if (projectThreads.length > 0) {
    return projectThreads.reduce(
      (latest, thread) => Math.max(latest, getThreadSortTimestamp(thread, sortOrder)),
      Number.NEGATIVE_INFINITY,
    );
  }

  if (sortOrder === "created_at") {
    return toSortableTimestamp(project.createdAt) ?? Number.NEGATIVE_INFINITY;
  }
  return toSortableTimestamp(project.updatedAt ?? project.createdAt) ?? Number.NEGATIVE_INFINITY;
}

export function sortProjectsForSidebar<
  TProject extends SidebarProject,
  TThread extends { projectId: Thread["projectId"] } & SidebarThreadSortInput,
>(
  projects: readonly TProject[],
  threads: readonly TThread[],
  sortOrder: SidebarProjectSortOrder,
): TProject[] {
  if (sortOrder === "manual") {
    return [...projects];
  }

  const threadsByProjectId = new Map<string, TThread[]>();
  for (const thread of threads) {
    const existing = threadsByProjectId.get(thread.projectId) ?? [];
    existing.push(thread);
    threadsByProjectId.set(thread.projectId, existing);
  }

  return [...projects].toSorted((left, right) => {
    const rightTimestamp = getProjectSortTimestamp(
      right,
      threadsByProjectId.get(right.id) ?? [],
      sortOrder,
    );
    const leftTimestamp = getProjectSortTimestamp(
      left,
      threadsByProjectId.get(left.id) ?? [],
      sortOrder,
    );
    const byTimestamp =
      rightTimestamp === leftTimestamp ? 0 : rightTimestamp > leftTimestamp ? 1 : -1;
    if (byTimestamp !== 0) return byTimestamp;
    return left.name.localeCompare(right.name) || left.id.localeCompare(right.id);
  });
}

/**
 * Threads that block removing a project, mirroring the server's
 * `requireProjectHasNoThreads` invariant.
 *
 * Archived threads do not count. The server accepts `project.delete` while
 * they exist, so rejecting it here left projects unremovable unless the user
 * deleted history they had deliberately archived.
 */
export function findThreadsBlockingProjectRemoval<
  T extends { projectId: string; archivedAt?: string | null },
>(threads: readonly T[], projectId: string): readonly T[] {
  return threads.filter((thread) => thread.projectId === projectId && thread.archivedAt == null);
}
